import { PropertyType } from '@app/models/enums'
import { IPropertyLead, PropertyLead } from '@app/models/PropertyLead'
import { ApplicationState } from '@app/store'
import { errorToast, successToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest, RequestMethod } from '@app/utils/request'
import { List, Record } from 'immutable'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------
export enum LeadsScope {
  All = 'All',
  Mine = 'Mine',
}

export interface ILeadsListState {
  readonly dueDate: string
  readonly error?: string
  readonly filterText: string
  readonly includeArchived: boolean
  readonly isLoading: boolean
  readonly isLoadingNew: boolean
  readonly itemChangedIndex?: number
  readonly items: List<IPropertyLead>
  readonly newItems: List<IPropertyLead>
  readonly priority: string
  readonly propertyTypes: List<PropertyType>
  readonly scope: LeadsScope
  readonly since: number
  readonly sortOrder: string
  readonly orderBy: string
  readonly status: string
  readonly totalItems: number
}

const LeadsListStateRecord = Record({
  dueDate: 'AllTime',
  error: '',
  filterText: '',
  includeArchived: false,
  isLoading: false,
  isLoadingNew: false,
  itemChangedIndex: undefined,
  items: List<PropertyLead>(),
  newItems: List<PropertyLead>(),
  priority: '',
  propertyTypes: List<PropertyType>(),
  scope: LeadsScope.Mine,
  since: 0,
  sortOrder: '',
  orderBy: 'Ascending',
  status: 'Incomplete',
  totalItems: 0,
})

export class LeadsListState
  extends LeadsListStateRecord
  implements ILeadsListState
{
  public static fromJS(initialState?: Partial<ILeadsListState>) {
    if (!initialState) {
      return new LeadsListState()
    }

    const { items, newItems, ...rest } = initialState
    return new LeadsListState({
      items: items
        ? List(items.map((i) => PropertyLead.fromJS(i)))
        : List<PropertyLead>(),
      newItems: newItems
        ? List(newItems.map((i) => PropertyLead.fromJS(i)))
        : List<PropertyLead>(),
      ...rest,
    })
  }

  public readonly dueDate: string
  public readonly error?: string
  public readonly filterText: string
  public readonly includeArchived: boolean
  public readonly isLoading: boolean
  public readonly isLoadingNew: boolean
  public readonly itemChangedIndex?: number
  public readonly items: List<PropertyLead>
  public readonly newItems: List<PropertyLead>
  public readonly priority: string
  public readonly propertyTypes: List<PropertyType>
  public readonly scope: LeadsScope
  public readonly since: number
  public readonly sortOrder: string
  public readonly orderBy: string
  public readonly status: string
  public readonly totalItems: number

  constructor(props?: Partial<LeadsListState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum LeadsListActionTypes {
  ArchiveLead = 'LEADS_LIST::ARCHIVE_LEAD',
  ArchiveLeadFailure = 'LEADS_LIST::ARCHIVE_LEAD_FAILURE',
  ArchiveLeadSuccess = 'LEADS_LIST::ARCHIVE_LEAD_SUCCESS',
  LoadLeads = 'LEADS_LIST::LOAD_LEADS',
  LoadLeadsFailure = 'LEADS_LIST::LOAD_LEADS_FAILURE',
  LoadLeadsRequest = 'LEADS_LIST::LOAD_LEADS_REQUEST',
  LoadLeadsSuccess = 'LEADS_LIST::LOAD_LEADS_SUCCESS',
  LoadNewLeads = 'LEADS_LIST::LOAD_NEW_LEADS',
  LoadNewLeadsFailure = 'LEADS_LIST::LOAD_NEW_LEADS_FAILURE',
  LoadNewLeadsRequest = 'LEADS_LIST::LOAD_NEW_LEADS_REQUEST',
  LoadNewLeadsSuccess = 'LEADS_LIST::LOAD_NEW_LEADS_SUCCESS',
  ResetItemChangedIndex = 'LEADS_LIST::RESET_ITEM_CHANGED_INDEX',
  SetDueDate = 'LEADS_LIST::SET_DUE_DATE',
  SetFilterText = 'LEADS_LIST::SET_FILTER_TEXT',
  SetIncludeArchived = 'LEADS_LIST:SET_INCLUDE_ARCHIVED',
  SetLeadPriority = 'LEADS_LIST::SET_LEAD_PRIORITY',
  SetLeadPrioritySuccess = 'LEADS_LIST::SET_LEAD_PRIORITY_SUCCESS',
  SetPriority = 'LEADS_LIST::SET_PRIORITY',
  SetPropertyTypes = 'LEADS_LIST::SET_PROPERTY_TYPES',
  SetScope = 'LEADS_LIST::SET_SCOPE',
  SetStatus = 'LEADS_LIST::SET_STATUS',
  SetSortOrder = 'LEADS_LIST::SET_SORT_ORDER',
  SetOrderBy = 'LEADS_LIST::SET_ORDER_BY',
  ShowNewLeads = 'LEADS_LIST::SHOW_NEW_LEADS',
}

interface IArchiveLeadAction {
  id: string
  type: LeadsListActionTypes.ArchiveLead
}

interface IArchiveLeadFailureAction {
  error: string
  type: LeadsListActionTypes.ArchiveLeadFailure
}

interface IArchiveLeadSuccessAction {
  id: string
  type: LeadsListActionTypes.ArchiveLeadSuccess
}

interface ILoadLeadsAction {
  append: boolean
  loadComplete?: () => void
  startIndex: number
  stopIndex: number
  type: LeadsListActionTypes.LoadLeads
}

interface ILoadLeadsFailureAction {
  error: string
  type: LeadsListActionTypes.LoadLeadsFailure
}

interface ILoadLeadsRequestAction {
  type: LeadsListActionTypes.LoadLeadsRequest
}

interface ILoadLeadsSuccessAction {
  append: boolean
  items: PropertyLead[]
  totalItems: number
  type: LeadsListActionTypes.LoadLeadsSuccess
}

interface ILoadNewLeadsAction {
  type: LeadsListActionTypes.LoadNewLeads
}

interface ILoadNewLeadsFailureAction {
  error: string
  type: LeadsListActionTypes.LoadNewLeadsFailure
}

interface ILoadNewLeadsRequestAction {
  type: LeadsListActionTypes.LoadNewLeadsRequest
}

interface ILoadNewLeadsSuccessAction {
  newItems: PropertyLead[]
  type: LeadsListActionTypes.LoadNewLeadsSuccess
}

interface IResetItemChangedIndexAction {
  type: LeadsListActionTypes.ResetItemChangedIndex
}

interface ISetDueDateAction {
  dueDate: string
  type: LeadsListActionTypes.SetDueDate
}

interface ISetFilterTextAction {
  filter: string
  type: LeadsListActionTypes.SetFilterText
}

interface ISetIncludeArchivedAction {
  include: boolean
  type: LeadsListActionTypes.SetIncludeArchived
}

interface ISetLeadPriorityAction {
  id: string
  type: LeadsListActionTypes.SetLeadPriority
  value: string
}

interface ISetLeadPrioritySuccessAction {
  id: string
  type: LeadsListActionTypes.SetLeadPrioritySuccess
  value: string
}

interface ISetOrderByAction {
  orderBy: string
  type: LeadsListActionTypes.SetOrderBy
}

interface ISetPriorityAction {
  type: LeadsListActionTypes.SetPriority
  value: string
}

interface ISetPropertyTypesAction {
  propertyTypes: List<PropertyType>
  type: LeadsListActionTypes.SetPropertyTypes
}

interface ISetScopeAction {
  scope: LeadsScope
  type: LeadsListActionTypes.SetScope
}

interface ISetSortOrderAction {
  sortOrder: string
  type: LeadsListActionTypes.SetSortOrder
}

interface ISetStatusAction {
  status: string
  type: LeadsListActionTypes.SetStatus
}

interface IShowNewLeadsAction {
  type: LeadsListActionTypes.ShowNewLeads
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | IArchiveLeadFailureAction
  | IArchiveLeadSuccessAction
  | ILoadLeadsFailureAction
  | ILoadLeadsRequestAction
  | ILoadLeadsSuccessAction
  | ILoadNewLeadsFailureAction
  | ILoadNewLeadsRequestAction
  | ILoadNewLeadsSuccessAction
  | ISetOrderByAction
  | IResetItemChangedIndexAction
  | ISetDueDateAction
  | ISetFilterTextAction
  | ISetIncludeArchivedAction
  | ISetLeadPrioritySuccessAction
  | ISetPriorityAction
  | ISetPropertyTypesAction
  | ISetScopeAction
  | ISetSortOrderAction
  | ISetStatusAction
  | IShowNewLeadsAction

// -------------------------
// ACTION CREATORS
// -------------------------
function archiveLead(id: string): IArchiveLeadAction {
  return {
    id,
    type: LeadsListActionTypes.ArchiveLead,
  }
}

function archiveLeadFailure(error: string): IArchiveLeadFailureAction {
  return {
    error,
    type: LeadsListActionTypes.ArchiveLeadFailure,
  }
}

function archiveLeadSuccess(id: string): IArchiveLeadSuccessAction {
  return {
    id,
    type: LeadsListActionTypes.ArchiveLeadSuccess,
  }
}

function loadLeads(
  startIndex: number,
  stopIndex: number,
  append: boolean,
  loadComplete?: () => void,
): ILoadLeadsAction {
  return {
    append,
    loadComplete,
    startIndex,
    stopIndex,
    type: LeadsListActionTypes.LoadLeads,
  }
}

function loadLeadsFailure(error: string): ILoadLeadsFailureAction {
  return {
    error,
    type: LeadsListActionTypes.LoadLeadsFailure,
  }
}

function loadLeadsRequest(): ILoadLeadsRequestAction {
  return {
    type: LeadsListActionTypes.LoadLeadsRequest,
  }
}

function loadLeadsSuccess(
  items: PropertyLead[],
  append: boolean,
  totalItems: number,
): ILoadLeadsSuccessAction {
  return {
    append,
    items,
    totalItems,
    type: LeadsListActionTypes.LoadLeadsSuccess,
  }
}

function loadNewLeads(): ILoadNewLeadsAction {
  return { type: LeadsListActionTypes.LoadNewLeads }
}

function loadNewLeadsFailure(error: string): ILoadNewLeadsFailureAction {
  return {
    error,
    type: LeadsListActionTypes.LoadNewLeadsFailure,
  }
}

function loadNewLeadsRequest(): ILoadNewLeadsRequestAction {
  return { type: LeadsListActionTypes.LoadNewLeadsRequest }
}

function loadNewLeadsSuccess(
  newItems: PropertyLead[],
): ILoadNewLeadsSuccessAction {
  return {
    newItems,
    type: LeadsListActionTypes.LoadNewLeadsSuccess,
  }
}

function resetItemChangedIndex(): IResetItemChangedIndexAction {
  return { type: LeadsListActionTypes.ResetItemChangedIndex }
}

function setDueDate(dueDate: string): ISetDueDateAction {
  return {
    dueDate,
    type: LeadsListActionTypes.SetDueDate,
  }
}

function setFilterText(filter: string): ISetFilterTextAction {
  return {
    filter,
    type: LeadsListActionTypes.SetFilterText,
  }
}

function setIncludeArchived(include: boolean): ISetIncludeArchivedAction {
  return {
    include,
    type: LeadsListActionTypes.SetIncludeArchived,
  }
}

function setLeadPriority(id: string, value: string): ISetLeadPriorityAction {
  return {
    id,
    type: LeadsListActionTypes.SetLeadPriority,
    value,
  }
}

function setLeadPrioritySuccess(
  id: string,
  value: string,
): ISetLeadPrioritySuccessAction {
  return {
    id,
    type: LeadsListActionTypes.SetLeadPrioritySuccess,
    value,
  }
}

function setOrderBy(orderBy: string): ISetOrderByAction {
  return {
    orderBy,
    type: LeadsListActionTypes.SetOrderBy,
  }
}

function setPriority(value: string): ISetPriorityAction {
  return {
    type: LeadsListActionTypes.SetPriority,
    value,
  }
}

function setPropertyTypes(
  propertyTypes: List<PropertyType>,
): ISetPropertyTypesAction {
  return {
    propertyTypes,
    type: LeadsListActionTypes.SetPropertyTypes,
  }
}

function setScope(scope: LeadsScope): ISetScopeAction {
  return {
    scope,
    type: LeadsListActionTypes.SetScope,
  }
}

function setSortOrder(sortOrder: string) {
  return {
    sortOrder,
    type: LeadsListActionTypes.SetSortOrder,
  }
}

function setStatus(status: string): ISetStatusAction {
  return {
    status,
    type: LeadsListActionTypes.SetStatus,
  }
}

function showNewLeads(): IShowNewLeadsAction {
  return { type: LeadsListActionTypes.ShowNewLeads }
}

export const actionCreators = {
  archiveLead,
  loadLeads,
  loadNewLeads,
  resetItemChangedIndex,
  setDueDate,
  setFilterText,
  setIncludeArchived,
  setLeadPriority,
  setOrderBy,
  setPriority,
  setPropertyTypes,
  setScope,
  setSortOrder,
  setStatus,
  showNewLeads,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<LeadsListState> = (
  state: LeadsListState = new LeadsListState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case LeadsListActionTypes.ArchiveLeadFailure:
      return state.set('error', action.error) as LeadsListState

    case LeadsListActionTypes.ArchiveLeadSuccess:
      const leadIndex = state.items.findIndex((item: any) => {
        return item.lead.id === action.id
      })

      if (leadIndex > -1) {
        return state.setIn(
          ['items', leadIndex, 'lead', 'isArchived'],
          true,
        ) as LeadsListState
      }

      return state

    case LeadsListActionTypes.LoadLeadsFailure:
      return state
        .set('error', action.error)
        .set('isLoading', false) as LeadsListState

    case LeadsListActionTypes.LoadLeadsRequest:
      return state.set('isLoading', true) as LeadsListState

    case LeadsListActionTypes.LoadLeadsSuccess: {
      const newItems = action.append
        ? (state.items.concat(List(action.items)) as List<PropertyLead>)
        : List(action.items)
      return state
        .set('error', undefined)
        .set('isLoading', false)
        .set('items', newItems)
        .set('newItems', !action.append ? List<PropertyLead>() : state.newItems)
        .set('since', Math.round(new Date().getTime() / 1000))
        .set('totalItems', action.totalItems) as LeadsListState
    }

    case LeadsListActionTypes.LoadNewLeadsFailure:
      return state
        .set('error', action.error)
        .set('isLoadingNew', false) as LeadsListState

    case LeadsListActionTypes.LoadNewLeadsRequest:
      return state.set('isLoadingNew', true) as LeadsListState

    case LeadsListActionTypes.LoadNewLeadsSuccess: {
      const newItems = action.newItems.filter(
        (i) =>
          state.items.findIndex(
            (si) => si !== undefined && si.lead.id === i.lead.id,
          ) === -1,
      )
      const existingNewItems = state.newItems.filter(
        (i) =>
          i !== undefined &&
          newItems.findIndex((ni) => ni.lead.id === i.lead.id) === -1,
      )
      const mergedNewItems = List(newItems).concat(existingNewItems).toList()
      return state
        .set('isLoadingNew', false)
        .set('newItems', mergedNewItems)
        .set('since', action.newItems[0].lead.dateCreated) as LeadsListState
    }

    case LeadsListActionTypes.SetOrderBy:
      return state.set('orderBy', action.orderBy) as LeadsListState

    case LeadsListActionTypes.ResetItemChangedIndex:
      return state.set('itemChangedIndex', undefined) as LeadsListState

    case LeadsListActionTypes.SetDueDate:
      return state.set('dueDate', action.dueDate) as LeadsListState

    case LeadsListActionTypes.SetFilterText:
      return state.set('filterText', action.filter) as LeadsListState

    case LeadsListActionTypes.SetIncludeArchived:
      return state.set('includeArchived', action.include) as LeadsListState

    case LeadsListActionTypes.SetLeadPrioritySuccess:
      const leadPriorityIndex = state.items.findIndex((item: any) => {
        return item.lead.id === action.id
      })

      if (leadPriorityIndex > -1) {
        return state.setIn(
          ['items', leadPriorityIndex, 'lead', 'leadPriority'],
          action.value,
        ) as LeadsListState
      }

      return state as LeadsListState

    case LeadsListActionTypes.SetPriority:
      return state.set('priority', action.value) as LeadsListState

    case LeadsListActionTypes.SetPropertyTypes:
      return state.set('propertyTypes', action.propertyTypes) as LeadsListState

    case LeadsListActionTypes.SetScope:
      return state.set('scope', action.scope) as LeadsListState

    case LeadsListActionTypes.SetSortOrder:
      return state.set('sortOrder', action.sortOrder) as LeadsListState

    case LeadsListActionTypes.SetStatus:
      return state.set('status', action.status) as LeadsListState

    case LeadsListActionTypes.ShowNewLeads:
      const lastLoadedItemTime =
        state.items.size > 0 ? state.items.first().lead.dateCreated : 0
      const newItemsCount = state.newItems.count(
        (i) => i !== undefined && i.lead.dateCreated > lastLoadedItemTime,
      )
      const existingItems = state.items
        .filter((i) => {
          return (
            i !== undefined &&
            state.newItems.findIndex(
              (ni) => ni !== undefined && ni.lead.id === i.lead.id,
            ) === -1
          )
        })
        .toList()
      return state
        .set('itemChangedIndex', 0)
        .set('items', state.newItems.concat(existingItems).toList())
        .set('newItems', List<PropertyLead>())
        .set('totalItems', state.totalItems + newItemsCount) as LeadsListState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* archiveLeadSaga(action: IArchiveLeadAction) {
  try {
    const baseUrl = `/api/Leads/${action.id}/archive`

    const opts = {
      method: RequestMethod.PUT,
    }

    const result = yield makeAuthenticatedRequest(baseUrl, opts)
    if (result.success === true) {
      yield put(successToast('Success', 'Lead successfully archived'))
      yield put(archiveLeadSuccess(action.id))
    } else {
      yield put(errorToast('Oops', result.message))
      yield put(archiveLeadFailure(result.message))
    }
  } catch (err) {
    yield put(errorToast('Oops', err.message))
    yield put(archiveLeadFailure(err.message))
  }
}

function* loadLeadsSaga(action: ILoadLeadsAction) {
  try {
    yield put(loadLeadsRequest())

    const currentState: LeadsListState = yield select(
      (state: ApplicationState) => state.leadsList,
    )

    const query: string[] = []

    query.push(`archived=${currentState.includeArchived}`)

    if (currentState.filterText) {
      query.push(`filter=${encodeURIComponent(currentState.filterText)}`)
    }

    if (currentState.priority) {
      query.push(`priority=${currentState.priority}`)
    }

    if (currentState.sortOrder !== '') {
      query.push(`sort=${currentState.sortOrder}`)
    }

    if (currentState.orderBy) {
      query.push(`orderBy=${currentState.orderBy}`)
    }

    if (currentState.status) {
      query.push(`status=${currentState.status}`)
    }

    if (currentState.dueDate) {
      query.push(`date=${currentState.dueDate}`)
    }

    if (currentState.propertyTypes && currentState.propertyTypes.count() > 0) {
      query.push(`propertyTypes=${currentState.propertyTypes.join('-')}`)
    }

    const count = action.stopIndex - action.startIndex + 1
    if (count > 0) {
      query.push(`count=${count}`)
    }

    if (action.startIndex > 0) {
      query.push(`skip=${action.startIndex}`)
    }

    if (action.append === true && currentState.items.size > 0) {
      let lastTime = 0

      if (currentState.items.last().lead) {
        lastTime = currentState.items.last().lead.dateCreated
      }

      if (
        currentState.newItems.last() &&
        currentState.newItems.last().lead &&
        currentState.newItems.last().lead.dateCreated < lastTime
      ) {
        lastTime = currentState.newItems.last().lead.dateCreated
      }

      query.push(`before=${lastTime}`)
    }

    const baseUrl =
      currentState.scope === LeadsScope.Mine
        ? '/api/Leads/MyPropertyLeads'
        : '/api/Leads/PropertyLeads'

    const url = `${baseUrl}${query.length > 0 ? `?${query.join('&')}` : ''}`

    const result = yield makeAuthenticatedRequest(url)

    if (result.success === true) {
      const contacts: PropertyLead[] = result.results.map((c: any) =>
        PropertyLead.fromJS(c),
      )
      yield put(loadLeadsSuccess(contacts, action.append, result.paging.total))
    } else {
      yield put(loadLeadsFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadLeadsFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* loadNewLeadsSaga(action: ILoadNewLeadsAction) {
  try {
    yield put(loadNewLeadsRequest())

    const currentState: LeadsListState = yield select(
      (state: ApplicationState) => state.leadsList,
    )

    const query: string[] = []

    query.push(`archived=${currentState.includeArchived}`)

    if (currentState.filterText) {
      query.push(`filter=${encodeURIComponent(currentState.filterText)}`)
    }

    if (currentState.priority) {
      query.push(`priority=${currentState.priority}`)
    }

    if (currentState.sortOrder !== '') {
      query.push(`sort=${currentState.sortOrder}`)
    }

    if (currentState.orderBy) {
      query.push(`orderBy=${currentState.orderBy}`)
    }

    if (currentState.status) {
      query.push(`status=${currentState.status}`)
    }

    if (currentState.dueDate) {
      query.push(`date=${currentState.dueDate}`)
    }

    if (currentState.propertyTypes) {
      query.push(`propertyTypes=${currentState.propertyTypes}`)
    }

    let firstItemTime = currentState.since
    const since = currentState.since

    if (currentState.newItems.size > 0) {
      const firstItem = currentState.newItems.first()

      firstItemTime = Math.max(since || 0, firstItem.lead.dateCreated)
    }

    query.push(`since=${firstItemTime}`)

    const baseUrl =
      currentState.scope === LeadsScope.Mine
        ? '/api/Leads/MyPropertyLeads'
        : '/api/Leads/PropertyLeads'

    const url = `${baseUrl}${query.length > 0 ? `?${query.join('&')}` : ''}`

    const result = yield makeAuthenticatedRequest(url)
    if (result.success === true) {
      const contacts: PropertyLead[] = result.results.map((c: any) =>
        PropertyLead.fromJS(c),
      )
      yield put(loadNewLeadsSuccess(contacts))
    } else {
      yield put(loadNewLeadsFailure(result.message))
    }
  } catch (err) {
    yield put(loadNewLeadsFailure(err.message))
  }
}

function* setPrioritySaga(action: ISetLeadPriorityAction) {
  try {
    const url = `/api/Leads/${action.id}`

    const currentState: LeadsListState = yield select(
      (state: ApplicationState) => state.leadsList,
    )

    const lead = currentState.items
      .find((a: any) => a.lead.id === action.id)
      .toJS()

    if (lead) {
      lead.lead.leadPriority = action.value
      const opts = {
        body: JSON.stringify(lead),
        method: RequestMethod.PUT,
      }

      const result = yield makeAuthenticatedRequest(url, opts)

      if (result.success === true) {
        yield put(setLeadPrioritySuccess(action.id, action.value))
      } else {
        yield put(errorToast('Oops!', result.message))
      }
    } else {
      yield put(errorToast('Oops!', 'Could not find the assignment'))
    }
  } catch (err) {
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchArchiveLead() {
  yield takeLatest(LeadsListActionTypes.ArchiveLead, archiveLeadSaga)
}

function* watchLoadLeads() {
  yield takeLatest(LeadsListActionTypes.LoadLeads, loadLeadsSaga)
}

function* watchLoadNewLeads() {
  yield takeLatest(LeadsListActionTypes.LoadNewLeads, loadNewLeadsSaga)
}

function* watchSetLeadPriority() {
  yield takeLatest(LeadsListActionTypes.SetLeadPriority, setPrioritySaga)
}

export function* leadsListRootSaga() {
  yield fork(watchArchiveLead)
  yield fork(watchLoadLeads)
  yield fork(watchLoadNewLeads)
  yield fork(watchSetLeadPriority)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState =
  () =>
  (state: ApplicationState): LeadsListState =>
    state.leadsList

export const selectError = () =>
  createSelector(selectModuleState(), (mState) => mState.error)

export const selectDueDate = () =>
  createSelector(selectModuleState(), (mState) => mState.dueDate)

export const selectFilterText = () =>
  createSelector(selectModuleState(), (mState) => mState.filterText)

export const selectIncludeArchived = () =>
  createSelector(selectModuleState(), (mState) => mState.includeArchived)

export const selectIsLoading = () =>
  createSelector(selectModuleState(), (mState) => mState.isLoading)

export const selectIsLoadingNew = () =>
  createSelector(selectModuleState(), (mState) => mState.isLoadingNew)

export const selectItemChangedIndex = () =>
  createSelector(selectModuleState(), (mState) => mState.itemChangedIndex)

export const selectItems = () =>
  createSelector(selectModuleState(), (mState) => mState.items)

export const selectNewItems = () =>
  createSelector(selectModuleState(), (mState) => mState.newItems)

export const selectOrderBy = () =>
  createSelector(selectModuleState(), (mstate) => mstate.orderBy)

export const selectPriority = () =>
  createSelector(selectModuleState(), (mState) => mState.priority)

export const selectPropertyTypes = () =>
  createSelector(selectModuleState(), (mState) => mState.propertyTypes)

export const selectScope = () =>
  createSelector(selectModuleState(), (mState) => mState.scope)

export const selectSince = () =>
  createSelector(selectModuleState(), (mState) => mState.since)

export const selectSortOrder = () =>
  createSelector(selectModuleState(), (mState) => mState.sortOrder)

export const selectStatus = () =>
  createSelector(selectModuleState(), (mState) => mState.status)

export const selectTotalItems = () =>
  createSelector(selectModuleState(), (mState) => mState.totalItems)
